import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { DataStampaField, oggiDmy } from "../components/DataStampaField";
import { LibreOfficeInstallButton } from "../components/LibreOfficeInstallButton";

export default function PrivacyTrattamentiPage() {
  const [selectedCodice, setSelectedCodice] = useState(null);
  if (selectedCodice) {
    return <DettaglioTrattamento codice={selectedCodice} onBack={() => setSelectedCodice(null)} />;
  }
  return <ListaTrattamenti onOpenDettaglio={setSelectedCodice} />;
}

// ============================================================
// LISTA TRATTAMENTI
// ============================================================
function ListaTrattamenti({ onOpenDettaglio }) {
  const [items, setItems] = useState([]);
  const [tipi, setTipi] = useState([]);
  const [tempiConservazione, setTempiConservazione] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [filterCategoria, setFilterCategoria] = useState(""); // "" = tutte, "__none__" = senza categoria, altro = nome esatto
  const [categorieOpen, setCategorieOpen] = useState(false);
  const [modelli, setModelli] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [descEditing, setDescEditing] = useState(null); // {codice, nome, descrizione} — modal popup descrizione
  const [stampaFor, setStampaFor] = useState(null); // codice trattamento per cui aprire la modal stampa
  const [anteprimaFor, setAnteprimaFor] = useState(null); // codice trattamento per cui aprire la modal anteprima
  const [copiaFor, setCopiaFor] = useState(null); // {codice, nome} sorgente per copia caratteristiche
  const [err, setErr] = useState(null);

  const carica = async () => {
    setLoading(true);
    try {
      const r = await api.get("/privacy/trattamenti", { params: search ? { search } : {} });
      setItems(r.data.items || []);
      const [rt, rc, rm, rcat] = await Promise.all([
        api.get("/privacy/lookup/tipo-trattamento"),
        api.get("/privacy/lookup/tempi-conservazione"),
        api.get("/privacy/modelli", { params: { ambito: "trattamento" } }),
        api.get("/privacy/trattamenti-categorie"),
      ]);
      setTipi(rt.data.items || []);
      setTempiConservazione(rc.data.items || []);
      setModelli(rm.data.items || []);
      setCategorie(rcat.data.items || []);
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, []);

  const reloadCategorie = async () => {
    try {
      const r = await api.get("/privacy/trattamenti-categorie");
      setCategorie(r.data.items || []);
    } catch { /* noop */ }
  };

  const salva = async () => {
    try {
      if (editing._isNew) {
        const payload = { ...editing };
        delete payload.codice;
        delete payload._isNew;
        await api.post("/privacy/trattamenti", payload);
      } else {
        await api.put(`/privacy/trattamenti/${encodeURIComponent(editing._orig_codice || editing.codice)}`, editing);
      }
      setEditing(null);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare trattamento "${r.nome}"?`)) return;
    try { await api.delete(`/privacy/trattamenti/${encodeURIComponent(r.codice)}`); await carica(); }
    catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const tipoLabel = (code) => tipi.find(t => t.codice === code)?.descrizione || code || "—";
  const tempoLabel = (code) => {
    if (!code) return "—";
    const t = tempiConservazione.find(x => x.codice === code);
    return t ? t.descrizione : code;
  };

  const itemsFiltered = useMemo(() => {
    if (!filterCategoria) return items;
    if (filterCategoria === "__none__") return items.filter(r => !(r.categoria || "").trim());
    return items.filter(r => (r.categoria || "") === filterCategoria);
  }, [items, filterCategoria]);

  const onCategoriaInlineChange = async (codice, newCat) => {
    // Update ottimistico + PUT
    setItems(rs => rs.map(r => r.codice === codice ? { ...r, categoria: newCat } : r));
    try {
      await api.put(`/privacy/trattamenti/${encodeURIComponent(codice)}`, { categoria: newCat });
    } catch (e) {
      // rollback
      const old = items.find(r => r.codice === codice);
      setItems(rs => rs.map(r => r.codice === codice ? { ...r, categoria: old?.categoria || "" } : r));
      setErr("Errore aggiornamento categoria: " + (e.response?.data?.detail || e.message));
    }
  };

  const stampaTrattamento = async (modCodice, formato, dataStampa) => {
    try {
      const url = `/privacy/modelli/${encodeURIComponent(modCodice)}/genera`;
      const r = await api.get(url, {
        params: { trattamento_codice: stampaFor, formato, data_stampa: dataStampa },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: r.headers["content-type"] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `${stampaFor}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setStampaFor(null);
    } catch (e) {
      // axios blob: il body è una blob, va letta per estrarre il messaggio
      let msg = "Errore generazione documento";
      try {
        const txt = await e.response?.data?.text?.();
        if (txt) {
          const j = JSON.parse(txt);
          msg = j.detail || msg;
        }
      } catch { /* ignore */ }
      setErr(msg);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <h2 style={{ color: "#37474f", fontSize: 18, margin: 0 }}>📋 Trattamenti · {itemsFiltered.length}{filterCategoria && <span style={{ fontSize: 12, fontWeight: 400, color: "#888" }}> / {items.length}</span>}</h2>
        <input placeholder="Cerca..." value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && carica()}
          data-testid="priv-tr-search"
          style={{ padding: 7, border: "1px solid #ccc", fontSize: 12, width: 220 }} />
        <button onClick={carica} style={{ padding: "7px 14px", background: "#455a64", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>🔍 Cerca</button>
        <select value={filterCategoria} onChange={e => setFilterCategoria(e.target.value)}
          data-testid="priv-tr-filter-categoria"
          title="Filtra per categoria"
          style={{ padding: "7px 8px", border: "1px solid #ccc", fontSize: 12, minWidth: 170, background: filterCategoria ? "#e3f2fd" : "white" }}>
          <option value="">— Tutte le categorie —</option>
          <option value="__none__">(senza categoria)</option>
          {categorie.filter(c => c.attivo).map(c => (
            <option key={c.id} value={c.nome}>{c.nome}</option>
          ))}
        </select>
        <button onClick={() => setCategorieOpen(true)} data-testid="priv-tr-cat-manage"
          title="Gestisci la tabella delle categorie (aggiungi · rinomina · elimina)"
          style={{ padding: "7px 12px", background: "#5d4037", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>⚙ Categorie</button>
        <button onClick={() => setEditing({ _isNew: true, codice: "", nome: "", descrizione: "", tipo_codice: "", categoria: "", tempo_conservazione: "" })}
          data-testid="priv-tr-new"
          style={{ marginLeft: "auto", padding: "7px 16px", background: "#37474f", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          ➕ Nuovo trattamento
        </button>
      </div>
      {err && <div style={{ padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, marginBottom: 10 }}>{err} <button onClick={() => setErr(null)} style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: "#c00", fontWeight: 700 }}>✕</button></div>}
      <div style={{ background: "white", border: "1px solid #ccc", overflowX: "auto" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 11, tableLayout: "auto", width: "100%" }}>
          <thead className="thead-blue">
            <tr>
              <th style={{ width: 32, padding: "4px 4px" }}>#</th>
              <th style={{ width: 70, padding: "4px 6px" }}>CODICE</th>
              <th style={{ minWidth: 180, padding: "4px 6px" }}>NOME</th>
              <th style={{ width: 80, padding: "4px 6px", textAlign: "center" }}>DESCR.</th>
              <th style={{ width: 110, padding: "4px 6px" }}>TIPO</th>
              <th style={{ width: 140, padding: "4px 6px" }}>CATEGORIA</th>
              <th style={{ width: 150, padding: "4px 6px" }}>CONSERVAZIONE</th>
              <th style={{ minWidth: 240, padding: "4px 6px", textAlign: "center" }} title="Contatori caratteristiche collegate (come le tab del dettaglio)">CARATTERISTICHE</th>
              <th style={{ width: 180, textAlign: "center", padding: "4px 4px" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
            {!loading && itemsFiltered.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#888" }}>{filterCategoria ? "Nessun trattamento per questo filtro." : "Nessun trattamento. Clicca "}<b>{!filterCategoria && "+ Nuovo trattamento"}</b>.</td></tr>
            )}
            {itemsFiltered.map((r, i) => (
              <tr key={r.codice} data-testid={`priv-tr-row-${i}`}>
                <td style={{ textAlign: "center", color: "#888", fontFamily: "Consolas, monospace", padding: "2px 4px" }}>{i + 1}</td>
                <td style={{ fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1", padding: "2px 6px" }}>{r.codice}</td>
                <td style={{ fontWeight: 600, padding: "2px 6px", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={r.nome}>{r.nome}</td>
                <td style={{ textAlign: "center", padding: "2px 4px" }}>
                  {(() => {
                    const hasDesc = !!(r.descrizione && r.descrizione.trim());
                    return (
                      <button
                        data-testid={`priv-tr-desc-${i}`}
                        onClick={() => setDescEditing({ codice: r.codice, nome: r.nome, descrizione: r.descrizione || "" })}
                        title={hasDesc ? `Descrizione:\n${r.descrizione}` : "Nessuna descrizione — clicca per aggiungerla"}
                        style={{
                          background: hasDesc ? "#1b5e20" : "#bdbdbd",
                          color: "white",
                          border: "none", padding: "3px 9px", fontSize: 11,
                          fontWeight: 700, cursor: "pointer", borderRadius: 3,
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}>
                        {hasDesc ? "📝 ✓" : "📝 +"}
                      </button>
                    );
                  })()}
                </td>
                <td style={{ padding: "2px 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 10 }}
                    title={tipoLabel(r.tipo_codice)}>{tipoLabel(r.tipo_codice)}</td>
                <td style={{ padding: "1px 4px" }}>
                  <select data-testid={`priv-tr-categoria-inline-${i}`}
                    value={r.categoria || ""}
                    onChange={e => onCategoriaInlineChange(r.codice, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    style={{ width: "100%", padding: "2px 4px", fontSize: 10, border: "1px solid #ddd", background: r.categoria ? "#e8f5e9" : "white" }}>
                    <option value="">—</option>
                    {categorie.filter(c => c.attivo).map(c => (
                      <option key={c.id} value={c.nome}>{c.nome}</option>
                    ))}
                    {r.categoria && !categorie.find(c => c.nome === r.categoria) && (
                      <option value={r.categoria}>{r.categoria}</option>
                    )}
                  </select>
                </td>
                <td style={{ padding: "2px 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 10 }}
                    title={tempoLabel(r.tempo_conservazione)}>{tempoLabel(r.tempo_conservazione)}</td>
                <td style={{ padding: "2px 4px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                  {(() => {
                    const ARCH = [
                      { k: "dislocazioni",           label: "DISL",  title: "Dislocazioni",            count: (r.dislocazioni_codici || []).length },
                      { k: "tipi_dato",              label: "DATI",  title: "Tipo Dato / Contenuto",   count: (r.tipi_dato_codici || []).length },
                      { k: "motivi",                 label: "MOT",   title: "Motivi",                  count: (r.motivi_codici || []).length },
                      { k: "fasi",                   label: "FASI",  title: "Fasi",                    count: (r.fasi_codici || []).length },
                      { k: "rischi",                 label: "RISC",  title: "Cause del Rischio",       count: (r.rischi_codici || []).length },
                      { k: "misure",                 label: "MIS",   title: "Misure di Sicurezza",     count: (r.misure_codici || []).length },
                      { k: "autorizzazioni",         label: "DOC",   title: "Autorizzazioni Docenti",  count: (r.autorizzazioni || []).length },
                      { k: "autorizzazioni_esterni", label: "EST",   title: "Autorizzazioni Esterni",  count: (r.autorizzazioni_esterni || []).length },
                    ];
                    return (
                      <div style={{ display: "inline-flex", flexWrap: "nowrap", gap: 3 }}>
                        {ARCH.map(a => (
                          <button key={a.k} data-testid={`priv-tr-cnt-${a.k}-${i}`}
                            onClick={() => onOpenDettaglio(r.codice)}
                            title={`${a.title}: ${a.count}`}
                            style={{
                              background: a.count > 0 ? "#0d47a1" : "#eceff1",
                              color: a.count > 0 ? "white" : "#90a4ae",
                              border: "none", borderRadius: 3, fontSize: 9, fontWeight: 800,
                              padding: "2px 5px", cursor: "pointer", minWidth: 42,
                              display: "inline-flex", flexDirection: "column", alignItems: "center",
                              lineHeight: 1.1, letterSpacing: 0.3,
                            }}>
                            <span style={{ fontSize: 8, opacity: a.count > 0 ? 0.85 : 0.7 }}>{a.label}</span>
                            <span style={{ fontSize: 12, fontWeight: 900 }}>{a.count}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td style={{ textAlign: "center", whiteSpace: "nowrap", padding: "2px 4px" }}>
                  <button onClick={() => setEditing({ ...r, _orig_codice: r.codice })}
                    data-testid={`priv-tr-edit-${i}`}
                    title="Modifica anagrafica trattamento"
                    style={{ background: "#1565c0", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>✏</button>
                  <button onClick={() => onOpenDettaglio(r.codice)} data-testid={`priv-tr-dettaglio-${i}`}
                    title="Apri dettaglio trattamento con tutte le sottoschede"
                    style={{ background: "#2e7d32", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>📋</button>
                  <button onClick={() => setCopiaFor({ codice: r.codice, nome: r.nome })} data-testid={`priv-tr-copia-${i}`}
                    title="Copia tutte le caratteristiche di questo trattamento su un altro"
                    style={{ background: "#00695c", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>📑</button>
                  <button onClick={() => { const g = window.location.pathname.startsWith("/g/admin") ? "admin" : "privacy"; window.location.href = `/g/${g}/privacy/fascicolo/trattamento/${encodeURIComponent(r.codice)}`; }}
                    data-testid={`priv-tr-fascicolo-${i}`}
                    title="Apri fascicolo (insieme dei documenti generati per questo trattamento)"
                    style={{ background: "#ef6c00", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>📁</button>
                  <button onClick={() => setAnteprimaFor(r.codice)} data-testid={`priv-tr-anteprima-${i}`}
                    title="Anteprima PDF del trattamento usando un modello"
                    style={{ background: "#6a1b9a", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>👁</button>
                  <button onClick={() => setStampaFor(r.codice)} data-testid={`priv-tr-stampa-${i}`}
                    title="Genera documento da modello"
                    style={{ background: "#c62828", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>🖨</button>
                  <button onClick={() => elimina(r)}
                    data-testid={`priv-tr-elimina-${i}`}
                    title="Elimina trattamento"
                    style={{ background: "#c62828", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <TrattamentoEditModal editing={editing} setEditing={setEditing} tipi={tipi} tempiConservazione={tempiConservazione} categorie={categorie} onSave={salva} onOpenCategorie={() => setCategorieOpen(true)} />}
      {copiaFor && <CopiaCaratteristicheModal source={copiaFor} items={items} onClose={() => setCopiaFor(null)} onDone={() => { setCopiaFor(null); carica(); }} />}
      {descEditing && (
        <DescrizioneModal
          editing={descEditing}
          setEditing={setDescEditing}
          onSave={async (nuovaDescrizione) => {
            try {
              await api.put(`/privacy/trattamenti/${encodeURIComponent(descEditing.codice)}`, {
                descrizione: nuovaDescrizione,
              });
              // update ottimistico nella lista
              setItems(rs => rs.map(it => it.codice === descEditing.codice ? { ...it, descrizione: nuovaDescrizione } : it));
              setDescEditing(null);
            } catch (e) {
              alert(e.response?.data?.detail || "Errore salvataggio descrizione");
            }
          }}
        />
      )}
      {categorieOpen && <TrattamentiCategorieModal categorie={categorie} onClose={() => setCategorieOpen(false)} onReload={reloadCategorie} />}
      {stampaFor && (
        <StampaModal modelli={modelli} onPick={stampaTrattamento} onClose={() => setStampaFor(null)} titleSuffix={stampaFor} />
      )}
      {anteprimaFor && (
        <AnteprimaModal modelli={modelli} trattamentoCodice={anteprimaFor} onClose={() => setAnteprimaFor(null)} />
      )}
    </div>
  );
}

// ============================================================
// MODAL EDIT (anagrafica base del trattamento)
// ============================================================
// ============================================================
// DESCRIZIONE MODAL — popup editor descrizione trattamento
// ============================================================
function DescrizioneModal({ editing, setEditing, onSave }) {
  const [val, setVal] = useState(editing.descrizione || "");
  const [saving, setSaving] = useState(false);
  return (
    <div data-testid="priv-desc-modal" onClick={() => setEditing(null)}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "min(720px, 92vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", borderRadius: 6, boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
        <div style={{ padding: "12px 18px", background: "#0d47a1", color: "white", borderRadius: "6px 6px 0 0", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>📝 Descrizione Trattamento</div>
            <div style={{ fontSize: 11, opacity: 0.85, fontFamily: "Consolas, monospace" }}>{editing.codice} — {editing.nome}</div>
          </div>
          <button data-testid="priv-desc-close" onClick={() => setEditing(null)}
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.4)", color: "white", padding: "4px 10px", fontSize: 12, cursor: "pointer", borderRadius: 3 }}>✕</button>
        </div>
        <div style={{ padding: 18, overflow: "auto" }}>
          <textarea
            data-testid="priv-desc-textarea"
            value={val}
            onChange={e => setVal(e.target.value)}
            autoFocus
            placeholder="Inserisci una descrizione del trattamento..."
            style={{ width: "100%", minHeight: 280, padding: 12, fontSize: 13, fontFamily: "inherit", border: "1px solid #ccc", borderRadius: 3, resize: "vertical", lineHeight: 1.5 }}
          />
          <div style={{ marginTop: 6, fontSize: 11, color: "#666" }}>{val.length} caratteri</div>
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button data-testid="priv-desc-cancel" onClick={() => setEditing(null)} disabled={saving}
            style={{ background: "#9e9e9e", color: "white", border: "none", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", borderRadius: 3 }}>
            Annulla
          </button>
          <button data-testid="priv-desc-save"
            onClick={async () => { setSaving(true); await onSave(val.trim()); setSaving(false); }}
            disabled={saving}
            style={{ background: "#2e7d32", color: "white", border: "none", padding: "8px 18px", fontSize: 12, fontWeight: 800, cursor: saving ? "not-allowed" : "pointer", borderRadius: 3 }}>
            {saving ? "⏳ Salvataggio..." : "💾 Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}


function TrattamentoEditModal({ editing, setEditing, tipi, tempiConservazione, categorie, onSave, onOpenCategorie }) {
  const setF = (k, v) => setEditing({ ...editing, [k]: v });
  return (
    <div onClick={() => setEditing(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", width: 680, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ background: "#37474f", color: "white", padding: "12px 18px", fontWeight: 700, fontSize: 14, display: "flex", justifyContent: "space-between" }}>
          {editing._isNew ? "➕ Nuovo Trattamento" : `✏ Modifica ${editing._orig_codice}`}
          <button onClick={() => setEditing(null)} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666" }}>CODICE</label>
              <input value={editing._isNew ? "— AUTO —" : (editing.codice || "")}
                disabled
                title={editing._isNew ? "Il codice viene generato automaticamente al salvataggio" : "Il codice non è modificabile"}
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace", fontWeight: 700, background: "#f5f5f5", color: editing._isNew ? "#888" : "#0d47a1" }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666" }}>NOME TRATTAMENTO *</label>
              <input value={editing.nome || ""} onChange={e => setF("nome", e.target.value)}
                data-testid="priv-tr-m-nome"
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 12 }} />
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: "#666" }}>DESCRIZIONE</label>
            <textarea value={editing.descrizione || ""} onChange={e => setF("descrizione", e.target.value)}
              data-testid="priv-tr-m-desc"
              rows={3}
              style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 12, resize: "vertical" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666" }}>TIPO TRATTAMENTO</label>
              <select value={editing.tipo_codice || ""} onChange={e => setF("tipo_codice", e.target.value)}
                data-testid="priv-tr-m-tipo"
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 12 }}>
                <option value="">—</option>
                {tipi.map(t => <option key={t.codice} value={t.codice}>{t.descrizione}</option>)}
              </select>
              <div style={{ fontSize: 9, color: "#888", marginTop: 3 }}>
                💡 Gestisci le opzioni in <b>Privacy → Tabelle → Tipo Trattamento</b>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666" }}>TEMPO DI CONSERVAZIONE</label>
              <select value={editing.tempo_conservazione || ""} onChange={e => setF("tempo_conservazione", e.target.value)}
                data-testid="priv-tr-m-tempo"
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 12 }}>
                <option value="">—</option>
                {(tempiConservazione || []).map(t => <option key={t.codice} value={t.codice}>{t.codice} — {t.descrizione}</option>)}
              </select>
              <div style={{ fontSize: 9, color: "#888", marginTop: 3 }}>
                💡 Gestisci le opzioni in <b>Privacy → Tabelle → Tempi di Conservazione</b>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: "#666" }}>CATEGORIA</label>
            <div style={{ display: "flex", gap: 6 }}>
              <select value={editing.categoria || ""} onChange={e => setF("categoria", e.target.value)}
                data-testid="priv-tr-m-categoria"
                style={{ flex: 1, padding: 7, border: "1px solid #ccc", fontSize: 12 }}>
                <option value="">— Nessuna categoria —</option>
                {(categorie || []).filter(c => c.attivo).map(c => (
                  <option key={c.id} value={c.nome}>{c.nome}</option>
                ))}
                {editing.categoria && !(categorie || []).find(c => c.nome === editing.categoria) && (
                  <option value={editing.categoria}>{editing.categoria} (valore attuale)</option>
                )}
              </select>
              <button type="button" onClick={onOpenCategorie}
                data-testid="priv-tr-m-cat-manage"
                title="Gestisci la tabella delle categorie"
                style={{ padding: "4px 10px", fontSize: 11, fontWeight: 700, background: "#5d4037", color: "white", border: "none", cursor: "pointer", borderRadius: 2 }}>
                ⚙ Gestisci
              </button>
            </div>
          </div>
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={() => setEditing(null)} style={{ padding: "6px 14px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
          <button onClick={onSave} data-testid="priv-tr-save"
            style={{ padding: "6px 18px", background: "#37474f", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>💾 Salva</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DETTAGLIO TRATTAMENTO — vista full-page con tab + shuttle box
// ============================================================
function CopiaCaratteristicheModal({ source, items, onClose, onDone }) {
  const [target, setTarget] = useState("");
  const [modo, setModo] = useState("sostituisci");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const candidates = (items || []).filter(t => t.codice !== source.codice);
  const filtered = filter.trim()
    ? candidates.filter(t => `${t.codice} ${t.nome}`.toLowerCase().includes(filter.trim().toLowerCase()))
    : candidates;

  const doCopy = async () => {
    if (!target) { setErr("Seleziona il trattamento di destinazione"); return; }
    const tgt = (items || []).find(t => t.codice === target);
    const label = tgt ? `${tgt.codice} — ${tgt.nome}` : target;
    const msg = modo === "sostituisci"
      ? `Copiare TUTTE le caratteristiche di ${source.codice} su "${label}"?\n\nLe caratteristiche attuali della destinazione verranno SOSTITUITE.`
      : `Aggiungere le caratteristiche di ${source.codice} a "${label}"?\n\nQuelle esistenti verranno mantenute.`;
    if (!window.confirm(msg)) return;
    setBusy(true); setErr(null);
    try {
      await api.post("/privacy/trattamenti/copia-caratteristiche", { da: source.codice, a: target, modo });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore durante la copia");
      setBusy(false);
    }
  };

  return (
    <div onMouseDown={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10001, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div data-testid="priv-copia-modal" onMouseDown={e => e.stopPropagation()} style={{ background: "white", borderRadius: 8, width: 520, maxWidth: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 48px rgba(0,0,0,0.35)", overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", background: "#00695c", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>📑 Copia caratteristiche</div>
          <button onClick={onClose} data-testid="priv-copia-close" style={{ background: "transparent", border: "none", color: "white", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 13, color: "#333" }}>Da: <b style={{ fontFamily: "Consolas, monospace", color: "#0d47a1" }}>{source.codice}</b> — {source.nome}</div>
          {err && <div style={{ padding: 8, background: "#fff0f0", color: "#c00", fontSize: 12 }}>{err}</div>}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 4 }}>Trattamento di destinazione</label>
            <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filtra per codice o nome…" data-testid="priv-copia-filter"
              style={{ width: "100%", padding: "7px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, marginBottom: 6 }} />
            <select value={target} onChange={e => setTarget(e.target.value)} data-testid="priv-copia-target" size={8}
              style={{ width: "100%", border: "1px solid #ccc", borderRadius: 4, fontSize: 12, padding: 2 }}>
              {filtered.map(t => <option key={t.codice} value={t.codice}>{t.codice} — {t.nome}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 4 }}>Modalità</label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginBottom: 3, cursor: "pointer" }}>
              <input type="radio" name="copia-modo" checked={modo === "sostituisci"} onChange={() => setModo("sostituisci")} data-testid="priv-copia-modo-sostituisci" />
              Sostituisci tutte le caratteristiche della destinazione
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
              <input type="radio" name="copia-modo" checked={modo === "aggiungi"} onChange={() => setModo("aggiungi")} data-testid="priv-copia-modo-aggiungi" />
              Aggiungi a quelle esistenti (unione)
            </label>
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} data-testid="priv-copia-cancel" style={{ padding: "8px 16px", background: "#eee", color: "#333", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer" }}>Annulla</button>
          <button onClick={doCopy} disabled={busy || !target} data-testid="priv-copia-confirm" style={{ padding: "8px 20px", background: (busy || !target) ? "#80cbc4" : "#00695c", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: (busy || !target) ? "not-allowed" : "pointer" }}>{busy ? "Copia…" : "📑 Copia"}</button>
        </div>
      </div>
    </div>
  );
}


const TABS = [
  { k: "dislocazioni",    label: "DISLOCAZIONI",                 lookup: "dislocazioni",   field: "dislocazioni_codici" },
  { k: "tipi_dato",       label: "TIPO DATO / CONTENUTO",        lookup: "tipi-dato",      field: "tipi_dato_codici" },
  { k: "motivi",          label: "MOTIVO",                       lookup: "motivi",         field: "motivi_codici" },
  { k: "fasi",            label: "FASI",                         lookup: "fasi",           field: "fasi_codici" },
  { k: "rischi",          label: "CAUSE DEL RISCHIO",            lookup: "rischi",         field: "rischi_codici" },
  { k: "misure",          label: "MISURE DI SICUREZZA ADOTTATE", lookup: "misure",         field: "misure_codici" },
  { k: "autorizzazioni",         label: "AUTORIZZAZIONI DOCENTI",   lookup: null,             field: null },
  { k: "autorizzazioni_esterni", label: "AUTORIZZAZIONI ESTERNI",   lookup: null,             field: null },
];

function DettaglioTrattamento({ codice, onBack }) {
  const [trattamento, setTrattamento] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS[0].k);
  const [lookups, setLookups] = useState({}); // { dislocazioni: [...], tipi_dato: [...], ... }
  const [categorie, setCategorie] = useState([]); // privacy_trattamenti_tipi (= Categoria Tipi Dato)
  const [tipoDatoFilter, setTipoDatoFilter] = useState(""); // filtro per categoria nel tab tipi_dato
  const [docenti, setDocenti] = useState([]); // elenco docenti attivi per tab autorizzazioni
  const [esterni, setEsterni] = useState([]); // elenco esterni attivi per tab autorizzazioni_esterni
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [preferiti, setPreferiti] = useState({}); // { dislocazioni_codici: [codici...], ... }
  const [availMode, setAvailMode] = useState("preferite"); // "preferite" | "tutte" — si apre su preferite

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const rt = await api.get(`/privacy/trattamenti/${encodeURIComponent(codice)}`);
        if (mounted) setTrattamento(rt.data);
      } catch (e) {
        if (mounted) setErr(e.response?.data?.detail || "Errore caricamento trattamento");
      }
      const results = await Promise.all(TABS.filter(t => t.lookup).map(async t => {
        try {
          const r = await api.get(`/privacy/lookup/${t.lookup}`);
          return [t.k, r.data.items || []];
        } catch { return [t.k, []]; }
      }));
      if (mounted) setLookups(Object.fromEntries(results));
      // Carico le categorie (Categoria Tipi Dato) per il filtro nel tab TIPO DATO
      try {
        const rcat = await api.get("/privacy/lookup/trattamenti-tipi");
        if (mounted) setCategorie(rcat.data.items || []);
      } catch { /* ignore */ }
      // Carico i docenti attivi per il tab AUTORIZZAZIONI
      try {
        const rd = await api.get("/docenti", { params: { limit: 500, attivo: true } });
        const list = (rd.data?.data || []).map(d => ({
          codice: String(d.codice || ""),
          cognome: d.cognome || "",
          nome: d.nome || "",
          tipo: d.tipo || "",
        })).filter(d => d.codice);
        list.sort((a, b) => (a.cognome + a.nome).localeCompare(b.cognome + b.nome));
        if (mounted) setDocenti(list);
      } catch { /* ignore */ }
      // Carico esterni attivi per il tab AUTORIZZAZIONI ESTERNI
      try {
        const re = await api.get("/privacy/esterni", { params: { attivi: true } });
        const list2 = (re.data?.items || []).map(e => ({
          codice: String(e.codice || ""),
          cognome: e.ragione_sociale || "",
          nome: e.referente ? `(ref. ${e.referente})` : "",
        })).filter(e => e.codice);
        list2.sort((a, b) => a.cognome.localeCompare(b.cognome));
        if (mounted) setEsterni(list2);
      } catch { /* ignore */ }
      // Carico i preferiti (codici collegati ad almeno un trattamento almeno una volta)
      try {
        const rp = await api.get("/privacy/preferiti");
        if (mounted) setPreferiti(rp.data || {});
      } catch { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [codice]);

  const currentTab = useMemo(() => TABS.find(t => t.k === activeTab), [activeTab]);

  // Ogni caratteristica si apre sulle Preferite; azzero anche il filtro categoria.
  useEffect(() => { setAvailMode("preferite"); setTipoDatoFilter(""); }, [activeTab]);

  const markFavorite = (field, cods) => {
    setPreferiti(prev => {
      const cur = new Set(prev[field] || []);
      cods.forEach(c => cur.add(c));
      return { ...prev, [field]: Array.from(cur) };
    });
  };

  const persist = async (field, arr) => {
    setSaving(true); setErr(null);
    try {
      await api.put(`/privacy/trattamenti/${encodeURIComponent(codice)}`, { [field]: arr });
      setSavedAt(new Date());
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio");
      // ricarico per ripristinare stato consistente
      try {
        const rt = await api.get(`/privacy/trattamenti/${encodeURIComponent(codice)}`);
        setTrattamento(rt.data);
      } catch { /* ignore */ }
    } finally {
      setSaving(false);
    }
  };

  const applyChange = (field, nextArr) => {
    setTrattamento({ ...trattamento, [field]: nextArr });
    persist(field, nextArr);
  };

  const isAutorizzazioni = activeTab === "autorizzazioni";
  const isAutorizzazioniEsterni = activeTab === "autorizzazioni_esterni";
  const items = lookups[activeTab] || [];
  const selectedCodici = (trattamento && currentTab?.field) ? (trattamento[currentTab.field] || []) : [];
  const selectedSet = new Set(selectedCodici);
  // Filtro categoria attivo solo per tab tipi_dato
  const availableBase = items.filter(it => !selectedSet.has(it.codice));
  const favSet = new Set((currentTab?.field && preferiti[currentTab.field]) || []);
  const favAvailCount = availableBase.filter(it => favSet.has(it.codice)).length;
  // Modalità Preferite: mostra solo i codici già collegati almeno una volta.
  const availableByMode = (availMode === "preferite")
    ? availableBase.filter(it => favSet.has(it.codice))
    : availableBase;
  const available = (activeTab === "tipi_dato" && tipoDatoFilter)
    ? availableByMode.filter(it => (it.categoria_codice || "") === tipoDatoFilter)
    : availableByMode;
  const selected = items.filter(it => selectedSet.has(it.codice));

  // Azioni shuttle box
  const moveToRight = (cod) => {
    if (!currentTab?.field) return;
    const next = Array.from(new Set([...(trattamento[currentTab.field] || []), cod]));
    markFavorite(currentTab.field, [cod]);
    applyChange(currentTab.field, next);
  };
  const moveToLeft = (cod) => {
    if (!currentTab?.field) return;
    const next = (trattamento[currentTab.field] || []).filter(x => x !== cod);
    applyChange(currentTab.field, next);
  };
  const moveAllToRight = () => {
    if (!currentTab?.field) return;
    // Sposta solo i disponibili VISIBILI (rispetta il filtro categoria/preferite se attivo)
    const visibleCodici = available.map(it => it.codice);
    const merged = Array.from(new Set([...(trattamento[currentTab.field] || []), ...visibleCodici]));
    markFavorite(currentTab.field, visibleCodici);
    applyChange(currentTab.field, merged);
  };
  const moveAllToLeft = () => {
    if (!currentTab?.field) return;
    applyChange(currentTab.field, []);
  };

  // ========================== AUTORIZZAZIONI ==========================
  const autorizzazioni = trattamento?.autorizzazioni || [];
  const applyAutorizzazioni = (nextArr) => {
    setTrattamento({ ...trattamento, autorizzazioni: nextArr });
    persist("autorizzazioni", nextArr);
  };
  const addDocente = (docCod) => {
    if (autorizzazioni.some(a => a.docente_codice === docCod)) return;
    applyAutorizzazioni([...autorizzazioni, {
      docente_codice: docCod,
      vedere: true, creare: false, modificare: false, cancellare: false,
    }]);
  };
  const removeDocente = (docCod) => {
    applyAutorizzazioni(autorizzazioni.filter(a => a.docente_codice !== docCod));
  };
  const toggleAutorizzazione = (docCod, perm) => {
    applyAutorizzazioni(autorizzazioni.map(a =>
      a.docente_codice === docCod ? { ...a, [perm]: !a[perm] } : a
    ));
  };
  const addAllDocenti = () => {
    const already = new Set(autorizzazioni.map(a => a.docente_codice));
    const added = docenti
      .filter(d => !already.has(d.codice))
      .map(d => ({ docente_codice: d.codice, vedere: true, creare: false, modificare: false, cancellare: false }));
    if (added.length) applyAutorizzazioni([...autorizzazioni, ...added]);
  };
  // Add multipli: aggiunge solo i codici specificati (usato dal filtro tipo)
  const addManyDocenti = (cods) => {
    const already = new Set(autorizzazioni.map(a => a.docente_codice));
    const added = (cods || [])
      .filter(c => !already.has(c))
      .map(c => ({ docente_codice: c, vedere: true, creare: false, modificare: false, cancellare: false }));
    if (added.length) applyAutorizzazioni([...autorizzazioni, ...added]);
  };
  // Includi intera categoria filtrata definendo i permessi: aggiorna gli esistenti e aggiunge i mancanti
  const includeManyDocenti = (cods, perms) => {
    const set = new Set(cods || []);
    if (!set.size) return;
    const p = { vedere: !!perms.vedere, creare: !!perms.creare, modificare: !!perms.modificare, cancellare: !!perms.cancellare };
    const present = new Set(autorizzazioni.map(a => a.docente_codice));
    const updated = autorizzazioni.map(a => set.has(a.docente_codice) ? { ...a, ...p } : a);
    const added = [...set].filter(c => !present.has(c)).map(c => ({ docente_codice: c, ...p }));
    applyAutorizzazioni([...updated, ...added]);
  };
  const clearAllAutorizzazioni = () => {
    if (!autorizzazioni.length) return;
    if (!window.confirm(`Rimuovere TUTTI i ${autorizzazioni.length} docenti autorizzati?`)) return;
    applyAutorizzazioni([]);
  };
  // Bulk: setta lo stesso permesso (true/false) a tutti i docenti autorizzati
  const bulkSetPermDocenti = (perm, value) => {
    if (!autorizzazioni.length) return;
    applyAutorizzazioni(autorizzazioni.map(a => ({ ...a, [perm]: !!value })));
  };

  // ========================== AUTORIZZAZIONI ESTERNI ==========================
  const autEst = trattamento?.autorizzazioni_esterni || [];
  const applyAutEst = (nextArr) => {
    setTrattamento({ ...trattamento, autorizzazioni_esterni: nextArr });
    persist("autorizzazioni_esterni", nextArr);
  };
  const addEsterno = (cod) => {
    if (autEst.some(a => a.esterno_codice === cod)) return;
    applyAutEst([...autEst, { esterno_codice: cod, vedere: true, creare: false, modificare: false, cancellare: false }]);
  };
  const removeEsterno = (cod) => applyAutEst(autEst.filter(a => a.esterno_codice !== cod));
  const toggleAutEst = (cod, perm) => applyAutEst(autEst.map(a => a.esterno_codice === cod ? { ...a, [perm]: !a[perm] } : a));
  const addAllEsterni = () => {
    const already = new Set(autEst.map(a => a.esterno_codice));
    const added = esterni.filter(e => !already.has(e.codice))
      .map(e => ({ esterno_codice: e.codice, vedere: true, creare: false, modificare: false, cancellare: false }));
    if (added.length) applyAutEst([...autEst, ...added]);
  };
  const addManyEsterni = (cods) => {
    const already = new Set(autEst.map(a => a.esterno_codice));
    const added = (cods || []).filter(c => !already.has(c))
      .map(c => ({ esterno_codice: c, vedere: true, creare: false, modificare: false, cancellare: false }));
    if (added.length) applyAutEst([...autEst, ...added]);
  };
  const includeManyEsterni = (cods, perms) => {
    const set = new Set(cods || []);
    if (!set.size) return;
    const p = { vedere: !!perms.vedere, creare: !!perms.creare, modificare: !!perms.modificare, cancellare: !!perms.cancellare };
    const present = new Set(autEst.map(a => a.esterno_codice));
    const updated = autEst.map(a => set.has(a.esterno_codice) ? { ...a, ...p } : a);
    const added = [...set].filter(c => !present.has(c)).map(c => ({ esterno_codice: c, ...p }));
    applyAutEst([...updated, ...added]);
  };
  const clearAllAutEst = () => {
    if (!autEst.length) return;
    if (!window.confirm(`Rimuovere TUTTI i ${autEst.length} esterni autorizzati?`)) return;
    applyAutEst([]);
  };
  const bulkSetPermEsterni = (perm, value) => {
    if (!autEst.length) return;
    applyAutEst(autEst.map(a => ({ ...a, [perm]: !!value })));
  };

  return (
    <div style={{ padding: 0, display: "flex", flexDirection: "column", height: "calc(100vh - 70px)" }}>
      {/* Header */}
      <div style={{ background: "#37474f", color: "white", padding: "12px 20px", display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={onBack} data-testid="priv-dett-back"
          style={{ background: "#546e7a", color: "white", border: "none", padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          ← Torna alla lista
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>📋 Dettaglio Trattamento</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            <span style={{ fontFamily: "Consolas, monospace", fontWeight: 700 }}>{codice}</span>
            {trattamento?.nome ? ` — ${trattamento.nome}` : ""}
          </div>
        </div>
        <div style={{ fontSize: 11 }}>
          {saving && <span>💾 Salvataggio...</span>}
          {!saving && savedAt && <span style={{ color: "#a5d6a7" }}>✓ Salvato alle {savedAt.toLocaleTimeString()}</span>}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", background: "#bbdefb", borderBottom: "2px solid #29b6f6", overflowX: "auto" }}>
        {TABS.map(t => {
          let count = 0;
          if (trattamento) {
            if (t.field) count = (trattamento[t.field] || []).length;
            else if (t.k === "autorizzazioni") count = (trattamento.autorizzazioni || []).length;
            else if (t.k === "autorizzazioni_esterni") count = (trattamento.autorizzazioni_esterni || []).length;
          }
          const isActive = activeTab === t.k;
          return (
            <div key={t.k} onClick={() => setActiveTab(t.k)} data-testid={`priv-dett-tab-${t.k}`}
              style={{
                padding: "10px 18px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                background: isActive ? "white" : "transparent",
                color: isActive ? "#0d47a1" : "#1565c0",
                borderBottom: isActive ? "3px solid #0d47a1" : "3px solid transparent",
                whiteSpace: "nowrap", textTransform: "uppercase",
              }}>
              {t.label}
              {count > 0 && <span style={{ marginLeft: 6, background: "#0d47a1", color: "white", fontSize: 10, padding: "1px 6px", borderRadius: 8 }}>{count}</span>}
            </div>
          );
        })}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "hidden", padding: 16, background: "#fafafa" }}>
        {err && <div style={{ padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, marginBottom: 10 }}>{err}</div>}
        {isAutorizzazioni ? (
          !trattamento ? (
            <div style={{ textAlign: "center", color: "#888", padding: 40 }}>Caricamento...</div>
          ) : (
            <AutorizzazioniPanel
              docenti={docenti}
              autorizzazioni={autorizzazioni}
              onAddDocente={addDocente}
              onRemoveDocente={removeDocente}
              onTogglePermesso={toggleAutorizzazione}
              onAddAll={addAllDocenti}
              onAddMany={addManyDocenti}
              onIncludeMany={includeManyDocenti}
              onClearAll={clearAllAutorizzazioni}
              onBulkSetPerm={bulkSetPermDocenti}
              tipoField="tipo"
              saving={saving}
            />
          )
        ) : isAutorizzazioniEsterni ? (
          !trattamento ? (
            <div style={{ textAlign: "center", color: "#888", padding: 40 }}>Caricamento...</div>
          ) : (
            <AutorizzazioniPanel
              docenti={esterni}
              autorizzazioni={autEst.map(a => ({ ...a, docente_codice: a.esterno_codice }))}
              onAddDocente={addEsterno}
              onRemoveDocente={removeEsterno}
              onTogglePermesso={toggleAutEst}
              onAddAll={addAllEsterni}
              onAddMany={addManyEsterni}
              onIncludeMany={includeManyEsterni}
              onClearAll={clearAllAutEst}
              onBulkSetPerm={bulkSetPermEsterni}
              saving={saving}
              labels={{
                colonnaSx: "ESTERNI DISPONIBILI",
                colonnaDx: "ESTERNI AUTORIZZATI",
                empty: "Tutti gli esterni sono autorizzati",
                placeholder: "🔍 Cerca ragione sociale, codice...",
                colHeader: "ESTERNO / FORNITORE",
              }}
            />
          )
        ) : !trattamento ? (
          <div style={{ textAlign: "center", color: "#888", padding: 40 }}>Caricamento...</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: "center", color: "#888", padding: 40 }}>
            Nessun record nella tabella <b>{currentTab.label}</b>.<br />
            Vai in <b>Privacy → Tabelle Privacy → {currentTab.label}</b> per popolarla.
          </div>
        ) : (
          <ShuttleBox
            available={available}
            selected={selected}
            onMoveToRight={moveToRight}
            onMoveToLeft={moveToLeft}
            onMoveAllRight={moveAllToRight}
            onMoveAllLeft={moveAllToLeft}
            saving={saving}
            tabKey={activeTab}
            favToggle={{ mode: availMode, onChange: setAvailMode, favCount: favAvailCount, allCount: availableBase.length }}
            categoryFilter={activeTab === "tipi_dato" ? {
              value: tipoDatoFilter,
              onChange: setTipoDatoFilter,
              options: categorie,
            } : null}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================
// ANTEPRIMA MODAL — preview PDF inline (iframe) prima di scaricare
// ============================================================
function AnteprimaModal({ modelli: initialModelli, trattamentoCodice, onClose }) {
  const [modelli, setModelli] = useState(initialModelli || []);
  const [sel, setSel] = useState(""); // nessun modello pre-selezionato → l'utente sceglie esplicitamente
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [err, setErr] = useState("");
  const [loMissing, setLoMissing] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [dataStampa, setDataStampa] = useState(oggiDmy());

  // Refetch sempre i modelli all'apertura, così se sono stati creati/importati
  // dopo il caricamento della pagina, l'utente li vede subito.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/privacy/modelli", { params: { ambito: "trattamento" } });
        if (alive) setModelli(r.data?.items || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  // Carica anteprima PDF quando cambia il modello o la data
  useEffect(() => {
    if (!sel) return;
    let alive = true;
    let createdUrl = "";
    (async () => {
      setLoading(true); setErr(""); setLoMissing(false);
      try {
        const r = await api.get(`/privacy/modelli/${encodeURIComponent(sel)}/genera`, {
          params: { trattamento_codice: trattamentoCodice, formato: "pdf", data_stampa: dataStampa },
          responseType: "blob",
        });
        if (!alive) return;
        const blob = new Blob([r.data], { type: "application/pdf" });
        createdUrl = URL.createObjectURL(blob);
        setPdfUrl(createdUrl);
      } catch (e) {
        if (!alive) return;
        let msg = "Errore generazione anteprima";
        try {
          const txt = await e.response?.data?.text?.();
          if (txt) { const j = JSON.parse(txt); msg = j.detail || msg; }
        } catch { /* ignore */ }
        if (e.response?.status === 503 || /LibreOffice/i.test(msg)) setLoMissing(true);
        setErr(msg);
        setPdfUrl("");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [sel, trattamentoCodice, dataStampa, reloadTick]);

  const scarica = async (formato) => {
    try {
      const r = await api.get(`/privacy/modelli/${encodeURIComponent(sel)}/genera`, {
        params: { trattamento_codice: trattamentoCodice, formato, data_stampa: dataStampa },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: r.headers["content-type"] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `${trattamentoCodice}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setErr("Errore download");
    }
  };

  return (
    <div onClick={onClose} data-testid="priv-anteprima-bg"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="priv-anteprima-modal"
        style={{ background: "white", width: "90vw", maxWidth: 1200, height: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 6px 30px rgba(0,0,0,0.4)" }}>
        <div style={{ padding: "12px 16px", background: "#6a1b9a", color: "white", display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>👁 Anteprima — Trattamento {trattamentoCodice}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.9 }} title="Data di stampa (placeholder «Data.Stampa»)">📅 Data:</span>
            <input type="text" value={dataStampa} onChange={e => setDataStampa(e.target.value)}
              placeholder="gg/mm/aaaa"
              data-testid="priv-ant-data-stampa"
              title="Data di stampa (placeholder «Data.Stampa»)"
              style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12, width: 100, color: "#37474f", background: "white" }} />
            <select value={sel} onChange={e => setSel(e.target.value)} data-testid="priv-ant-modello-select"
              style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12, minWidth: 280, color: "#37474f", background: "white" }}>
              <option value="">— scegli un modello —</option>
              {modelli.map(m => (
                <option key={m.codice} value={m.codice}>{m.codice} — {m.nome}{m.filename ? "" : " (no file)"}</option>
              ))}
            </select>
            <button onClick={() => scarica("docx")} disabled={!sel || loading} data-testid="priv-ant-docx"
              style={{ padding: "5px 12px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: !sel || loading ? "not-allowed" : "pointer", opacity: !sel || loading ? 0.5 : 1 }}>📄 DOCX</button>
            <button onClick={() => scarica("pdf")} disabled={!sel || loading} data-testid="priv-ant-pdf"
              style={{ padding: "5px 12px", background: "#c62828", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: !sel || loading ? "not-allowed" : "pointer", opacity: !sel || loading ? 0.5 : 1 }}>📕 PDF</button>
            <button onClick={onClose} data-testid="priv-ant-close"
              style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer", padding: "0 4px" }}>✕</button>
          </div>
        </div>

        <div style={{ flex: 1, background: "#37474f", position: "relative", overflow: "hidden" }}>
          {loading && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 13, fontWeight: 700 }}>
              ⏳ Generazione anteprima in corso...
            </div>
          )}
          {!loading && err && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, padding: 20 }}>
              <div style={{ background: "#c62828", color: "white", padding: "12px 18px", fontSize: 12, fontWeight: 700, maxWidth: 600, textAlign: "center" }}>{err}</div>
              {loMissing && (
                <div style={{ background: "white", padding: "12px 18px", borderRadius: 4, textAlign: "center" }}>
                  <LibreOfficeInstallButton onReady={() => { setLoMissing(false); setErr(""); setReloadTick(t => t + 1); }} />
                </div>
              )}
            </div>
          )}
          {!loading && !err && pdfUrl && (
            <iframe src={pdfUrl} title="Anteprima PDF" data-testid="priv-ant-iframe"
              style={{ width: "100%", height: "100%", border: "none", background: "white" }} />
          )}
          {!loading && !err && !pdfUrl && !sel && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: "#cfd8dc", fontSize: 14 }}>
              <div style={{ fontSize: 48 }}>📄</div>
              <div style={{ fontWeight: 700 }}>Scegli un modello dall'alto per generare l'anteprima</div>
              {modelli.length === 0 && (
                <div style={{ fontSize: 12, color: "#ff8a80" }}>Nessun modello disponibile. Caricalo da Privacy → Catalogo Docx.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STAMPA MODAL — scegli modello e formato
// ============================================================
function StampaModal({ modelli: initialModelli, onPick, onClose, titleSuffix }) {
  const [modelli, setModelli] = useState(initialModelli || []);
  const [sel, setSel] = useState(initialModelli?.[0]?.codice || "");
  const [dataStampa, setDataStampa] = useState(oggiDmy());

  // Refetch all'apertura per essere sempre allineati al backend
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/privacy/modelli", { params: { ambito: "trattamento" } });
        if (!alive) return;
        const list = r.data?.items || [];
        setModelli(list);
        if (!sel && list.length > 0) setSel(list[0].codice);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  return (
    <div onClick={onClose} data-testid="priv-stampa-modal-bg"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid="priv-stampa-modal"
        style={{ background: "white", padding: 20, minWidth: 480, maxWidth: 600, boxShadow: "0 6px 30px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: "#0d47a1" }}>
          🖨 Stampa con modello{titleSuffix ? ` — ${titleSuffix}` : ""}
        </div>
        <DataStampaField value={dataStampa} onChange={setDataStampa} testid="priv-stampa-data" />
        <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>MODELLO</div>
        <select value={sel} onChange={e => setSel(e.target.value)} data-testid="priv-stampa-modello-select"
          style={{ width: "100%", padding: "6px 10px", border: "1px solid #ccc", fontSize: 12, marginBottom: 16 }}>
          {modelli.map(m => (
            <option key={m.codice} value={m.codice}>
              {m.codice} — {m.nome} {m.filename ? `(${m.filename})` : "(nessun file)"}
            </option>
          ))}
        </select>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} data-testid="priv-stampa-cancel"
            style={{ padding: "8px 16px", background: "white", border: "1px solid #999", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Annulla
          </button>
          <button onClick={() => onPick(sel, "docx", dataStampa)} disabled={!sel} data-testid="priv-stampa-docx"
            style={{ padding: "8px 16px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: sel ? "pointer" : "not-allowed" }}>
            📄 Scarica DOCX
          </button>
          <button onClick={() => onPick(sel, "pdf", dataStampa)} disabled={!sel} data-testid="priv-stampa-pdf"
            style={{ padding: "8px 16px", background: "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: sel ? "pointer" : "not-allowed" }}>
            📕 Scarica PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SHUTTLE BOX — doppia lista con spostamento records
// ============================================================
function ShuttleBox({ available, selected, onMoveToRight, onMoveToLeft, onMoveAllRight, onMoveAllLeft, saving, tabKey, categoryFilter, favToggle }) {
  const seg = (active) => ({
    padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
    border: "1px solid #ffb74d", borderRadius: 0,
    background: active ? "#fb8c00" : "white", color: active ? "white" : "#e65100",
  });
  const favEmpty = favToggle && favToggle.mode === "preferite" && favToggle.favCount === 0;
  const availEmptyText = favEmpty
    ? "Nessuna opzione preferita (mai collegata ad una caratteristica). Premi «Tutte» per vedere l'elenco completo."
    : (categoryFilter?.value ? "Nessun record per questa categoria (o già tutti selezionati)" : "Tutti i record sono già selezionati");
  const availHeader = (favToggle || categoryFilter) ? (
    <>
      {favToggle && (
        <div style={{ padding: "8px 10px", background: "#fff3e0", borderBottom: "1px solid #ffe0b2", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#e65100", whiteSpace: "nowrap" }}>MOSTRA:</span>
          <div style={{ display: "flex" }}>
            <button onClick={() => favToggle.onChange("preferite")} data-testid={`priv-dett-fav-preferite-${tabKey}`}
              title="Mostra solo le opzioni già collegate ad una caratteristica almeno una volta"
              style={{ ...seg(favToggle.mode === "preferite"), borderRadius: "3px 0 0 3px" }}>★ Preferite ({favToggle.favCount})</button>
            <button onClick={() => favToggle.onChange("tutte")} data-testid={`priv-dett-fav-tutte-${tabKey}`}
              title="Mostra tutte le opzioni della lista"
              style={{ ...seg(favToggle.mode === "tutte"), borderLeft: "none", borderRadius: "0 3px 3px 0" }}>Tutte ({favToggle.allCount})</button>
          </div>
        </div>
      )}
      {categoryFilter && (
        <div style={{ padding: "8px 10px", background: "#e3f2fd", borderBottom: "1px solid #bbdefb", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#0d47a1", whiteSpace: "nowrap" }}>FILTRO CATEGORIA:</span>
          <select value={categoryFilter.value} onChange={e => categoryFilter.onChange(e.target.value)}
            data-testid={`priv-dett-filter-${tabKey}`}
            style={{ flex: 1, padding: "4px 6px", border: "1px solid #90caf9", fontSize: 11, background: "white" }}>
            <option value="">— tutte le categorie —</option>
            {(categoryFilter.options || []).map(c => (
              <option key={c.codice} value={c.codice}>{c.codice} — {c.descrizione}</option>
            ))}
          </select>
          {categoryFilter.value && (
            <button onClick={() => categoryFilter.onChange("")}
              title="Rimuovi filtro"
              style={{ background: "none", border: "1px solid #90caf9", color: "#0d47a1", padding: "3px 6px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>✕</button>
          )}
        </div>
      )}
    </>
  ) : null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 1fr", gap: 12, height: "100%" }}>
      {/* SINISTRA — Disponibili */}
      <ListPanel
        title={`DISPONIBILI · ${available.length}`}
        items={available}
        onItemClick={onMoveToRight}
        emptyText={availEmptyText}
        arrow="›"
        color="#1565c0"
        bg="white"
        saving={saving}
        tabKey={tabKey}
        side="avail"
        header={availHeader}
      />

      {/* CENTRO — Pulsanti di spostamento globali */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
        <button onClick={onMoveAllRight} disabled={saving || available.length === 0}
          data-testid={`priv-dett-all-right-${tabKey}`}
          title="Sposta TUTTI i record VISIBILI a destra"
          style={{ padding: "8px 4px", background: available.length === 0 ? "#cfd8dc" : "#2e7d32", color: "white", border: "none", fontSize: 14, fontWeight: 700, cursor: available.length === 0 ? "not-allowed" : "pointer", minWidth: 60 }}>
          ›› Tutti
        </button>
        <button onClick={onMoveAllLeft} disabled={saving || selected.length === 0}
          data-testid={`priv-dett-all-left-${tabKey}`}
          title="Rimuovi TUTTE le associazioni"
          style={{ padding: "8px 4px", background: selected.length === 0 ? "#cfd8dc" : "#c62828", color: "white", border: "none", fontSize: 14, fontWeight: 700, cursor: selected.length === 0 ? "not-allowed" : "pointer", minWidth: 60 }}>
          ‹‹ Tutti
        </button>
        <div style={{ fontSize: 10, color: "#666", textAlign: "center", marginTop: 8, lineHeight: 1.3 }}>
          oppure<br />click<br />singolo
        </div>
      </div>

      {/* DESTRA — Selezionati */}
      <ListPanel
        title={`SELEZIONATI · ${selected.length}`}
        items={selected}
        onItemClick={onMoveToLeft}
        emptyText="Nessun record selezionato. Clicca gli elementi a sinistra per associarli."
        arrow="‹"
        color="#2e7d32"
        bg="#e8f5e9"
        saving={saving}
        tabKey={tabKey}
        side="sel"
      />
    </div>
  );
}

function ListPanel({ title, items, onItemClick, emptyText, arrow, color, bg, saving, tabKey, side, header }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", border: `1px solid ${color}`, background: "white", overflow: "hidden" }}>
      <div style={{ background: color, color: "white", padding: "8px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
        {title}
      </div>
      {header}
      <div style={{ flex: 1, overflowY: "auto", background: bg }}>
        {items.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 12 }}>{emptyText}</div>
        ) : (
          items.map(it => (
            <div key={it.codice} onClick={() => !saving && onItemClick(it.codice)}
              data-testid={`priv-dett-${side}-${tabKey}-${it.codice}`}
              style={{
                padding: "8px 12px", borderBottom: "1px solid #eee", cursor: saving ? "wait" : "pointer",
                display: "flex", alignItems: "flex-start", gap: 8,
                transition: "background 0.1s",
              }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.background = "#fff9c4"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
              <span style={{ color, fontWeight: 700, fontSize: 16, lineHeight: 1 }}>{arrow}</span>
              <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                <div style={{ fontFamily: "Consolas, monospace", fontSize: 10, fontWeight: 700, color: "#0d47a1" }}>
                  {it.codice}{it.nome ? ` · ${it.nome}` : ""}
                </div>
                <div style={{ color: "#444", lineHeight: 1.35 }}>{it.descrizione}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================
// AUTORIZZAZIONI PANEL — matrice docenti × permessi (V/C/M/D)
// ============================================================
function AutorizzazioniPanel({ docenti, autorizzazioni, onAddDocente, onRemoveDocente, onTogglePermesso, onAddAll, onAddMany, onIncludeMany, onClearAll, onBulkSetPerm, tipoField, saving, labels }) {
  const L = labels || {};
  const lblSx = L.colonnaSx || "DOCENTI DISPONIBILI";
  const lblDx = L.colonnaDx || "DOCENTI AUTORIZZATI";
  const lblEmpty = L.empty || "Tutti i docenti sono autorizzati";
  const lblPh = L.placeholder || "🔍 Cerca cognome, nome o codice...";
  const lblItem = L.colHeader || "DOCENTE";
  const [filter, setFilter] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [inclPerm, setInclPerm] = useState({ vedere: true, creare: false, modificare: false, cancellare: false });
  const autMap = new Map(autorizzazioni.map(a => [a.docente_codice, a]));
  const available = docenti.filter(d => !autMap.has(d.codice));
  const selected = docenti.filter(d => autMap.has(d.codice));
  const filterLower = filter.trim().toLowerCase();
  // Lista distinta dei tipi per il dropdown filtro (solo se tipoField è impostato)
  const tipiDistinti = useMemo(() => {
    if (!tipoField) return [];
    const s = new Set();
    docenti.forEach(d => { const t = (d?.[tipoField] || "").trim(); if (t) s.add(t); });
    return Array.from(s).sort((a, b) => a.localeCompare(b, "it", { sensitivity: "base" }));
  }, [docenti, tipoField]);
  const availableFiltered = available.filter(d => {
    if (tipoField && tipoFilter && (d?.[tipoField] || "") !== tipoFilter) return false;
    if (filterLower && !(d.cognome + " " + d.nome + " " + d.codice).toLowerCase().includes(filterLower)) return false;
    return true;
  });
  const hasFilter = !!((tipoField && tipoFilter) || filterLower);
  // Intera categoria filtrata (inclusi i già autorizzati) per l'inclusione con permessi definiti
  const categoryFiltered = docenti.filter(d => {
    if (tipoField && tipoFilter && (d?.[tipoField] || "") !== tipoFilter) return false;
    if (filterLower && !(d.cognome + " " + d.nome + " " + d.codice).toLowerCase().includes(filterLower)) return false;
    return true;
  });

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 2fr", gap: 12, height: "100%" }}>
      {/* SINISTRA — Docenti disponibili */}
      <div style={{ display: "flex", flexDirection: "column", border: "1px solid #1565c0", background: "white", overflow: "hidden" }}>
        <div style={{ background: "#1565c0", color: "white", padding: "8px 12px", fontSize: 11, fontWeight: 700 }}>
          {lblSx} · {availableFiltered.length}{filter && available.length !== availableFiltered.length ? ` / ${available.length}` : ""}
        </div>
        <div style={{ padding: "6px 8px", background: "#e3f2fd", borderBottom: "1px solid #bbdefb" }}>
          <input value={filter} onChange={e => setFilter(e.target.value)}
            placeholder={lblPh}
            data-testid="priv-dett-aut-filter"
            style={{ width: "100%", padding: "5px 8px", border: "1px solid #90caf9", fontSize: 11, boxSizing: "border-box" }} />
          {tipoField && (
            <select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)}
              data-testid="priv-dett-aut-tipo-filter"
              title={tipiDistinti.length > 0
                ? "Filtra disponibili per Tipo docente"
                : "Nessun Tipo configurato nell'anagrafica docenti — vai in Docenti per assegnarli"}
              disabled={tipiDistinti.length === 0}
              style={{ width: "100%", marginTop: 4, padding: "4px 6px", border: "1px solid #90caf9", fontSize: 11, boxSizing: "border-box", background: tipoFilter ? "#fff59d" : "white" }}>
              <option value="">{tipiDistinti.length === 0 ? "— nessun Tipo configurato —" : "— tutti i tipi —"}</option>
              {tipiDistinti.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {availableFiltered.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 12 }}>
              {filter ? "Nessuna corrispondenza al filtro" : lblEmpty}
            </div>
          ) : (
            availableFiltered.map(d => (
              <div key={d.codice} onClick={() => !saving && onAddDocente(d.codice)}
                data-testid={`priv-dett-aut-avail-${d.codice}`}
                style={{
                  padding: "7px 12px", borderBottom: "1px solid #eee", cursor: saving ? "wait" : "pointer",
                  display: "flex", alignItems: "center", gap: 8, fontSize: 12,
                }}
                onMouseEnter={e => { if (!saving) e.currentTarget.style.background = "#fff9c4"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                <span style={{ color: "#1565c0", fontWeight: 700, fontSize: 16, lineHeight: 1 }}>›</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{d.cognome} {d.nome}</div>
                  <div style={{ fontFamily: "Consolas, monospace", fontSize: 9, color: "#888" }}>Cod: {d.codice}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* CENTRO — azioni globali */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
        {onIncludeMany && (
          <div data-testid="priv-dett-aut-incl-perms" style={{ width: "100%", border: "1px solid #c8e6c9", background: "#f1f8e9", borderRadius: 4, padding: "5px 6px" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#2e7d32", textAlign: "center", marginBottom: 3 }}>PERMESSI DA APPLICARE</div>
            {[["vedere", "Vedere"], ["creare", "Creare"], ["modificare", "Modificare"], ["cancellare", "Cancellare"]].map(([k, lbl]) => (
              <label key={k} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, cursor: "pointer", padding: "1px 2px" }}>
                <input type="checkbox" checked={!!inclPerm[k]} data-testid={`priv-dett-aut-incl-${k}`}
                  onChange={e => setInclPerm(p => ({ ...p, [k]: e.target.checked }))} />
                {lbl}
              </label>
            ))}
          </div>
        )}
        <button onClick={() => {
            if (onIncludeMany) {
              onIncludeMany(categoryFiltered.map(d => d.codice), inclPerm);
            } else if (hasFilter && onAddMany) {
              onAddMany(availableFiltered.map(d => d.codice));
            } else {
              onAddAll();
            }
          }}
          disabled={saving || (onIncludeMany ? categoryFiltered.length === 0 : availableFiltered.length === 0)}
          data-testid="priv-dett-aut-all-right"
          title={onIncludeMany
            ? `Includi ${categoryFiltered.length} ${hasFilter ? "elementi della categoria filtrata" : "elementi (tutti)"} con i permessi selezionati`
            : (hasFilter ? `Autorizza i ${availableFiltered.length} elementi visibili (filtro attivo)` : "Autorizza TUTTI")}
          style={{ padding: "8px 6px", background: (onIncludeMany ? categoryFiltered.length === 0 : availableFiltered.length === 0) ? "#cfd8dc" : "#2e7d32", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: (onIncludeMany ? categoryFiltered.length === 0 : availableFiltered.length === 0) ? "not-allowed" : "pointer", minWidth: 60, width: "100%" }}>
          ›› Includi {onIncludeMany ? (hasFilter ? `categoria (${categoryFiltered.length})` : `tutti (${categoryFiltered.length})`) : (hasFilter ? "Filtrati" : "Tutti")}
        </button>
        <button onClick={onClearAll} disabled={saving || selected.length === 0}
          data-testid="priv-dett-aut-all-left"
          title="Rimuovi TUTTE le autorizzazioni"
          style={{ padding: "8px 6px", background: selected.length === 0 ? "#cfd8dc" : "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: selected.length === 0 ? "not-allowed" : "pointer", minWidth: 60, width: "100%" }}>
          ‹‹ Rimuovi tutti
        </button>
        <div style={{ fontSize: 10, color: "#666", textAlign: "center", marginTop: 4, lineHeight: 1.3 }}>
          oppure click singolo
        </div>
      </div>

      {/* DESTRA — Tabella docenti autorizzati con permessi */}
      <div style={{ display: "flex", flexDirection: "column", border: "1px solid #2e7d32", background: "white", overflow: "hidden" }}>
        <div style={{ background: "#2e7d32", color: "white", padding: "8px 12px", fontSize: 11, fontWeight: 700 }}>
          {lblDx} · {selected.length}
        </div>
        {/* Bulk-set permessi: applica lo stesso permesso a TUTTI gli autorizzati in un click */}
        {onBulkSetPerm && selected.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, padding: "6px 8px", background: "#fff8e1", borderBottom: "1px solid #ffe082", alignItems: "center" }}
               data-testid="priv-dett-aut-bulk-bar">
            <span style={{ fontSize: 10, fontWeight: 700, color: "#6d4c41", marginRight: 4 }}>Applica a TUTTI ({selected.length}):</span>
            {[
              { p: "vedere",     l: "👁 VEDERE",     bg: "#1565c0" },
              { p: "creare",     l: "➕ CREARE",     bg: "#2e7d32" },
              { p: "modificare", l: "✏ MODIFICARE", bg: "#ef6c00" },
              { p: "cancellare", l: "🗑 CANCELLARE", bg: "#c62828" },
            ].map(b => (
              <div key={b.p} style={{ display: "inline-flex", gap: 2 }}>
                <button onClick={() => onBulkSetPerm(b.p, true)} disabled={saving}
                  data-testid={`priv-dett-aut-bulk-${b.p}-on`}
                  title={`Imposta ${b.p.toUpperCase()} = ON per tutti gli autorizzati`}
                  style={{ background: b.bg, color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: "3px 0 0 3px" }}>
                  + {b.l}
                </button>
                <button onClick={() => onBulkSetPerm(b.p, false)} disabled={saving}
                  data-testid={`priv-dett-aut-bulk-${b.p}-off`}
                  title={`Imposta ${b.p.toUpperCase()} = OFF per tutti gli autorizzati`}
                  style={{ background: "white", color: b.bg, border: `1px solid ${b.bg}`, padding: "3px 5px", fontSize: 10, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: "0 3px 3px 0" }}>
                  −
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto", background: "#e8f5e9" }}>
          {selected.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#888", fontSize: 12 }}>
              Nessun elemento autorizzato.<br />Clicca a sinistra per autorizzare.
            </div>
          ) : (
            <table className="sw-table sw-table-compact" style={{ fontSize: 11, width: "100%" }}>
              <thead style={{ background: "#c8e6c9", position: "sticky", top: 0 }}>
                <tr>
                  <th style={{ width: 30 }}></th>
                  <th style={{ textAlign: "left" }}>{lblItem}</th>
                  <th style={{ width: 70, textAlign: "center" }} title="Vedere">👁 VEDERE</th>
                  <th style={{ width: 70, textAlign: "center" }} title="Creare">➕ CREARE</th>
                  <th style={{ width: 80, textAlign: "center" }} title="Modificare">✏ MODIFICARE</th>
                  <th style={{ width: 85, textAlign: "center" }} title="Cancellare">🗑 CANCELLARE</th>
                </tr>
              </thead>
              <tbody>
                {selected.map(d => {
                  const a = autMap.get(d.codice);
                  return (
                    <tr key={d.codice} data-testid={`priv-dett-aut-row-${d.codice}`}>
                      <td style={{ textAlign: "center" }}>
                        <button onClick={() => !saving && onRemoveDocente(d.codice)} disabled={saving}
                          data-testid={`priv-dett-aut-remove-${d.codice}`}
                          title="Rimuovi autorizzazione"
                          style={{ background: "#c62828", color: "white", border: "none", padding: "2px 5px", fontSize: 9, fontWeight: 700, cursor: saving ? "wait" : "pointer" }}>✕</button>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{d.cognome} {d.nome}</div>
                        <div style={{ fontFamily: "Consolas, monospace", fontSize: 9, color: "#888" }}>Cod: {d.codice}</div>
                      </td>
                      {["vedere", "creare", "modificare", "cancellare"].map(perm => (
                        <td key={perm} style={{ textAlign: "center" }}>
                          <input type="checkbox" checked={!!a?.[perm]}
                            onChange={() => onTogglePermesso(d.codice, perm)}
                            disabled={saving}
                            data-testid={`priv-dett-aut-${perm}-${d.codice}`}
                            style={{ cursor: saving ? "wait" : "pointer", width: 16, height: 16 }} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// MODAL GESTIONE CATEGORIE TRATTAMENTO
// ============================================================
function TrattamentiCategorieModal({ categorie, onClose, onReload }) {
  const [nuovo, setNuovo] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingNome, setEditingNome] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const aggiungi = async () => {
    const nome = nuovo.trim();
    if (!nome) return;
    setSaving(true); setErr(null);
    try {
      await api.post("/privacy/trattamenti-categorie", { nome, ord: categorie.length });
      setNuovo("");
      await onReload();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const inizioEdit = (c) => {
    setEditingId(c.id);
    setEditingNome(c.nome);
  };

  const salvaEdit = async (c) => {
    const nome = editingNome.trim();
    if (!nome || nome === c.nome) {
      setEditingId(null);
      return;
    }
    setSaving(true); setErr(null);
    try {
      await api.put(`/privacy/trattamenti-categorie/${c.id}`, { nome, ord: c.ord, attivo: c.attivo });
      setEditingId(null);
      await onReload();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const elimina = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.nome}"?`)) return;
    setSaving(true); setErr(null);
    try {
      await api.delete(`/privacy/trattamenti-categorie/${c.id}`);
      await onReload();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="priv-tr-categorie-modal"
        style={{ background: "white", width: "100%", maxWidth: 540, borderRadius: 4, overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#5d4037", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>⚙ Gestione Categorie Trattamento</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 14, overflow: "auto", flex: 1 }}>
          {err && <div style={{ background: "#fff0f0", color: "#c00", padding: 8, fontSize: 12, marginBottom: 10 }}>{err}</div>}
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <input value={nuovo} onChange={e => setNuovo(e.target.value)} placeholder="Nuova categoria (es. Risorse Umane)"
              data-testid="priv-tr-cat-input-new"
              onKeyDown={e => { if (e.key === "Enter") aggiungi(); }}
              style={{ flex: 1, padding: "6px 8px", fontSize: 12, border: "1px solid #bbb" }} />
            <button onClick={aggiungi} disabled={saving || !nuovo.trim()} data-testid="priv-tr-cat-add-btn"
              style={{ padding: "6px 14px", fontSize: 12, fontWeight: 700, background: "#2e7d32", color: "white", border: "none", cursor: saving ? "wait" : "pointer", borderRadius: 2 }}>
              + Aggiungi
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: "#f5f5f5" }}>
              <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Nome</th>
              <th style={{ padding: "6px 8px", textAlign: "center", width: 160, borderBottom: "1px solid #ddd" }}>Azioni</th>
            </tr></thead>
            <tbody>
              {categorie.length === 0 && (
                <tr><td colSpan={2} style={{ padding: 14, textAlign: "center", color: "#888" }}>Nessuna categoria definita</td></tr>
              )}
              {categorie.map(c => (
                <tr key={c.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "6px 8px" }}>
                    {editingId === c.id ? (
                      <input value={editingNome} onChange={e => setEditingNome(e.target.value)}
                        autoFocus onKeyDown={e => { if (e.key === "Enter") salvaEdit(c); if (e.key === "Escape") setEditingId(null); }}
                        data-testid={`priv-tr-cat-edit-${c.id}`}
                        style={{ width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid #1565c0" }} />
                    ) : (
                      <span style={{ color: c.attivo ? "#222" : "#888", fontWeight: 600 }}>{c.nome}</span>
                    )}
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                    {editingId === c.id ? (
                      <>
                        <button onClick={() => salvaEdit(c)} disabled={saving} data-testid={`priv-tr-cat-save-${c.id}`}
                          style={{ marginRight: 4, padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "#2e7d32", color: "white", border: "none", cursor: "pointer" }}>Salva</button>
                        <button onClick={() => setEditingId(null)}
                          style={{ padding: "3px 8px", fontSize: 10, background: "#eee", border: "1px solid #ccc", cursor: "pointer" }}>Annulla</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => inizioEdit(c)} data-testid={`priv-tr-cat-edit-btn-${c.id}`}
                          style={{ marginRight: 4, padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "#1565c0", color: "white", border: "none", cursor: "pointer" }}>✏</button>
                        <button onClick={() => elimina(c)} data-testid={`priv-tr-cat-del-btn-${c.id}`}
                          style={{ padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "#c62828", color: "white", border: "none", cursor: "pointer" }}>🗑</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

